import React, { useState, useEffect } from 'react';
import { 
  Users, BarChart3, Settings, CreditCard, TrendingUp, 
  Calendar, Zap, Eye, MessageSquare, Image, Clock,
  RefreshCw, CheckCircle, XCircle, Edit, Trash2
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const MarketingAdmin = () => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Subscription form
  const [subUserId, setSubUserId] = useState('');
  const [subPlan, setSubPlan] = useState('basic');
  const [subDays, setSubDays] = useState(30);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, settingsRes] = await Promise.all([
        api.get('/marketing/admin/stats'),
        api.get('/marketing/admin/users'),
        api.get('/marketing/admin/settings')
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users || []);
      setSettings(settingsRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Failed to fetch marketing data');
    }
    setLoading(false);
  };

  const handleUpdateSubscription = async (e) => {
    e.preventDefault();
    if (!subUserId) {
      toast.error('Enter user ID');
      return;
    }
    try {
      await api.post(`/marketing/admin/subscription?user_id=${subUserId}&plan=${subPlan}&duration_days=${subDays}`);
      toast.success('Subscription updated!');
      setSubUserId('');
      fetchData();
    } catch (err) {
      toast.error('Failed to update subscription');
    }
  };

  const handleToggleFeature = async (feature) => {
    if (!settings) return;
    const newSettings = {
      ...settings,
      features_enabled: {
        ...settings.features_enabled,
        [feature]: !settings.features_enabled[feature]
      }
    };
    try {
      await api.put('/marketing/admin/settings', newSettings);
      setSettings(newSettings);
      toast.success(`Feature ${newSettings.features_enabled[feature] ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error('Failed to update settings');
    }
  };

  const handleUpdatePlan = async (plan, field, value) => {
    if (!settings) return;
    const newSettings = {
      ...settings,
      plans: {
        ...settings.plans,
        [plan]: {
          ...settings.plans[plan],
          [field]: parseInt(value) || 0
        }
      }
    };
    try {
      await api.put('/marketing/admin/settings', newSettings);
      setSettings(newSettings);
      toast.success('Plan updated!');
    } catch (err) {
      toast.error('Failed to update plan');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'subscriptions', label: 'Subscriptions', icon: CreditCard },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-purple-400" />
            Marketing AI Admin
          </h1>
          <p className="text-gray-400 text-sm">Manage AstraPulse marketing tool</p>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-700 pb-2">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-colors ${
              activeTab === tab.id
                ? 'bg-purple-500/20 text-purple-400 border-b-2 border-purple-500'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-xl p-6 border border-purple-500/30">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-purple-400" />
              <div>
                <p className="text-gray-400 text-sm">Total Clients</p>
                <p className="text-2xl font-bold text-white">{stats.total_clients}</p>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-xl p-6 border border-blue-500/30">
            <div className="flex items-center gap-3">
              <Calendar className="w-8 h-8 text-blue-400" />
              <div>
                <p className="text-gray-400 text-sm">Campaigns</p>
                <p className="text-2xl font-bold text-white">{stats.total_campaigns}</p>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-xl p-6 border border-green-500/30">
            <div className="flex items-center gap-3">
              <Zap className="w-8 h-8 text-green-400" />
              <div>
                <p className="text-gray-400 text-sm">Total Posts</p>
                <p className="text-2xl font-bold text-white">{stats.total_posts}</p>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-orange-500/20 to-yellow-500/20 rounded-xl p-6 border border-orange-500/30">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-orange-400" />
              <div>
                <p className="text-gray-400 text-sm">Scheduled</p>
                <p className="text-2xl font-bold text-white">{stats.scheduled_posts}</p>
              </div>
            </div>
          </div>

          {/* Subscription Breakdown */}
          <div className="col-span-full bg-gray-800/50 rounded-xl p-6 border border-gray-700">
            <h3 className="text-white font-semibold mb-4">Subscription Breakdown</h3>
            <div className="grid grid-cols-4 gap-4">
              {Object.entries(stats.revenue_breakdown || {}).map(([plan, count]) => (
                <div key={plan} className="text-center">
                  <p className="text-2xl font-bold text-white">{count}</p>
                  <p className="text-gray-400 text-sm capitalize">{plan}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="bg-gray-800/50 rounded-xl border border-gray-700">
          <div className="p-4 border-b border-gray-700">
            <h3 className="text-white font-semibold">Marketing Tool Users</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-900/50">
                <tr>
                  <th className="text-left text-gray-400 text-sm p-4">User ID</th>
                  <th className="text-left text-gray-400 text-sm p-4">Clients</th>
                  <th className="text-left text-gray-400 text-sm p-4">Joined</th>
                  <th className="text-left text-gray-400 text-sm p-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user, i) => (
                  <tr key={i} className="border-t border-gray-700 hover:bg-gray-700/30">
                    <td className="p-4 text-white font-mono text-sm">{user._id}</td>
                    <td className="p-4 text-white">{user.client_count}</td>
                    <td className="p-4 text-gray-400 text-sm">
                      {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="p-4">
                      <button
                        onClick={() => setSubUserId(user._id)}
                        className="text-purple-400 hover:text-purple-300 text-sm"
                      >
                        Manage Sub
                      </button>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan={4} className="p-8 text-center text-gray-500">
                      No users yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Subscriptions Tab */}
      {activeTab === 'subscriptions' && settings && (
        <div className="space-y-6">
          {/* Update Subscription Form */}
          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
            <h3 className="text-white font-semibold mb-4">Update User Subscription</h3>
            <form onSubmit={handleUpdateSubscription} className="flex flex-wrap gap-4">
              <input
                type="text"
                placeholder="User ID"
                value={subUserId}
                onChange={e => setSubUserId(e.target.value)}
                className="flex-1 min-w-[200px] bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white"
              />
              <select
                value={subPlan}
                onChange={e => setSubPlan(e.target.value)}
                className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white"
              >
                <option value="free">Free</option>
                <option value="basic">Basic (₹499)</option>
                <option value="pro">Pro (₹1499)</option>
                <option value="enterprise">Enterprise (₹4999)</option>
              </select>
              <input
                type="number"
                placeholder="Days"
                value={subDays}
                onChange={e => setSubDays(parseInt(e.target.value))}
                className="w-24 bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white"
              />
              <button
                type="submit"
                className="px-6 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600"
              >
                Update
              </button>
            </form>
          </div>

          {/* Plan Limits */}
          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
            <h3 className="text-white font-semibold mb-4">Plan Limits Configuration</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(settings.plans || {}).map(([plan, limits]) => (
                <div key={plan} className="bg-gray-900/50 rounded-lg p-4 border border-gray-700">
                  <h4 className="text-white font-medium capitalize mb-3">{plan}</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Clients</span>
                      <input
                        type="number"
                        value={limits.clients}
                        onChange={e => handleUpdatePlan(plan, 'clients', e.target.value)}
                        className="w-20 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Posts/Day</span>
                      <input
                        type="number"
                        value={limits.posts_per_day}
                        onChange={e => handleUpdatePlan(plan, 'posts_per_day', e.target.value)}
                        className="w-20 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Campaigns</span>
                      <input
                        type="number"
                        value={limits.campaigns}
                        onChange={e => handleUpdatePlan(plan, 'campaigns', e.target.value)}
                        className="w-20 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Price (₹)</span>
                      <input
                        type="number"
                        value={limits.price}
                        onChange={e => handleUpdatePlan(plan, 'price', e.target.value)}
                        className="w-20 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && settings && (
        <div className="space-y-6">
          {/* Feature Toggles */}
          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
            <h3 className="text-white font-semibold mb-4">Feature Toggles</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Object.entries(settings.features_enabled || {}).map(([feature, enabled]) => (
                <div
                  key={feature}
                  className="flex items-center justify-between p-4 bg-gray-900/50 rounded-lg border border-gray-700"
                >
                  <span className="text-white capitalize">{feature.replace(/_/g, ' ')}</span>
                  <button
                    onClick={() => handleToggleFeature(feature)}
                    className={`w-12 h-6 rounded-full transition-colors ${
                      enabled ? 'bg-green-500' : 'bg-gray-600'
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded-full bg-white transition-transform ${
                        enabled ? 'translate-x-6' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* AI Models */}
          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
            <h3 className="text-white font-semibold mb-4">AI Models Configuration</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(settings.ai_models || {}).map(([purpose, model]) => (
                <div key={purpose} className="p-4 bg-gray-900/50 rounded-lg border border-gray-700">
                  <p className="text-gray-400 text-sm capitalize mb-1">{purpose}</p>
                  <p className="text-white font-mono text-sm">{model}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketingAdmin;
